/**
 * Links and redirects built from data the app does not control (API rows, agent output, MCP/OAuth metadata).
 *
 * WHY. React 18 only WARNS on a `javascript:` href, and `window.open` / `location.assign` never check at all, so
 * one hostile value from upstream ran script as the app (security report F2, 2026-10-13). A regex over the raw
 * string is not enough: browsers strip tabs and newlines from URLs, so `java\nscript:` slips past a
 * `^scheme:` test and still runs. The WHATWG parser applies the same stripping the browser does, so its
 * `protocol` is the scheme that would actually execute.
 *
 * A new tab always gets `noopener,noreferrer`: without it the opened page holds `window.opener` and can
 * redirect the app's own tab (to a fake login page, say).
 */

const WEB_PROTOCOLS = new Set(["http:", "https:"]);

/**
 * The URL as a string when it is http(s) — absolute, or relative to this app — else null.
 * `mailto:` is allowed only when `allowMailto` (a link, never a redirect target).
 */
export function safeWebUrl(raw: unknown, { allowMailto = false }: { allowMailto?: boolean } = {}): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value) return null;
  let url: URL;
  try {
    url = new URL(value, globalThis.location?.origin);
  } catch {
    return null;
  }
  if (WEB_PROTOCOLS.has(url.protocol) || (allowMailto && url.protocol === "mailto:")) return url.href;
  return null;
}

/** The same check for an OAuth/authorize hop, which must leave over TLS. */
export function safeHttpsUrl(raw: unknown): string | null {
  const href = safeWebUrl(raw);
  return href && href.startsWith("https:") ? href : null;
}

/** Open a vetted URL in a new tab with no handle back to the app. False when the URL was refused. */
export function openExternalUrl(raw: unknown): boolean {
  const href = safeWebUrl(raw);
  if (!href) return false;
  window.open(href, "_blank", "noopener,noreferrer");
  return true;
}
